import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { generateResponseScheme, type RespondedType, type RespondedWith, type ResponseError } from '@/models/response';
import { type CreatedTag, createdTagScheme } from '@/models/user/tag';
import ROUTES from '@/routes';
import { makePrompt } from '@/stores/prompter';
import { addToast, addTranslateToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import { tagListQueryOptions } from './use-tags';

export default function useCreateTag() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const mutation = useMutation<RespondedWith<CreatedTag>, ResponseError, string>({
    mutationFn: (tagName) =>
      fetcher<RespondedType>(
        '/CreateTag',
        { TagName: tagName },
        { schema: generateResponseScheme(createdTagScheme), validResponseCodes: [0] },
      ),
    onSuccess: (data) => {
      addTranslateToast({
        type: 'success',
        message: 'common.add-success',
      });

      queryClient.setQueryData(tagListQueryOptions.queryKey, (state) =>
        produce(state, (draft) => {
          if (draft?.Data && data.Data) {
            draft.Data.Tags.unshift({
              TagCount: 0,
              TagName: data.Data.Name,
              Id: data.Data.Id,
              TagMember: [],
            });
            draft.Data.Tags.sort((a, b) => b.Id - a.Id);
          }

          return draft;
        }),
      );
    },
    onError: (error) => {
      addToast({
        type: 'error',
        message: error.message,
      });
    },
  });

  const mutateAsyncWithPrompt = (navigateToTag = false) =>
    makePrompt({
      key: 'create-tag',
      type: 'form',
      title: t('contact.tags.add-tag'),
      meta: {
        schema: createTagFormSchema,
      },
      promise: async (resolution) => {
        const result = await createTagFormSchema.safeParseAsync(resolution);

        if (result.success) {
          const response = await mutation.mutateAsync(result.data.tagName);

          const tagId = response.Data?.Id;
          if (navigateToTag && tagId) {
            navigate(ROUTES.CONTACT.TAGS.BY_ID.$buildPath({ params: { tagId } }));
          }

          return response.Data?.Id;
        }
      },
    });

  return {
    ...mutation,
    mutateAsyncWithPrompt,
  };
}

const createTagFormSchema = z.object({
  tagName: z
    .string()
    .trim()
    .min(1, 'errors.string.min-length|count=1')
    .max(12, 'errors.string.max-length|count=12')
    .describe('contact.tags.tag-name'),
});
